/** GET/POST /api/rfq — camelCase from API. */

import type { MaterialItem, ProcurementRequest } from './procurementRequest';
import { mapMaterialItemFromApi, primaryMaterialLine } from './procurementRequest';

/** True when RFQ materials are all Tubular or String design (extended vendor quote fields). */
export function isTubularStringQuoteRfq(materials: MaterialItem[]): boolean {
  return (
    materials.length > 0 &&
    materials.every((m) => m.kind === 'Tubular' || m.kind === 'StringDesign')
  );
}

/** Normalize RFQ status from API (handles PascalCase, camelCase, extra spaces). */
export function normalizeRfqStatus(s: string | undefined | null): string {
  return (s ?? '').trim().toLowerCase();
}

/** Maps domain enum integers (1–9) to the same slugs as string JSON. */
const RFQ_STATUS_ENUM_TO_SLUG: Record<number, string> = {
  1: 'draft',
  2: 'requestsent',
  3: 'partialreceived',
  4: 'received',
  5: 'pendingapproval',
  6: 'approved',
  7: 'rejected',
  8: 'cancelled',
  9: 'complete'
};

/**
 * Normalizes RFQ status from API JSON (string names, PascalCase, or numeric enum).
 * Without this, `9` becomes `"9"` instead of `"complete"` and UI logic breaks.
 */
export function normalizeRfqStatusFromApi(raw: unknown): string {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const n = Math.trunc(raw);
    if (n >= 1 && n <= 9) {
      return RFQ_STATUS_ENUM_TO_SLUG[n] ?? String(n);
    }
  }
  const s = String(raw ?? '').trim();
  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    if (n >= 1 && n <= 9) {
      return RFQ_STATUS_ENUM_TO_SLUG[n] ?? s.toLowerCase();
    }
  }
  return normalizeRfqStatus(s);
}

/** GET /api/rfq list row (summary). */
export interface RequestForQuoteSummary {
  id: string;
  rfqNumber: string;
  procurementRequestId: string;
  requestNumber: string;
  region: string;
  status: string;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export function rfqDetailToSummary(d: RequestForQuoteDetail): RequestForQuoteSummary {
  return {
    id: d.id,
    rfqNumber: d.rfqNumber,
    procurementRequestId: d.procurementRequestId,
    requestNumber: d.requestNumber,
    region: d.region,
    status: d.status,
    createdAtUtc: d.createdAtUtc,
    updatedAtUtc: d.updatedAtUtc
  };
}

/**
 * Linked procurement request document id (not the RFQ id). Supports camelCase and PascalCase JSON.
 * Never derive from `raw.id` — that is the RFQ document id.
 */
export function procurementRequestIdFromRfqApi(raw: Record<string, unknown>): string {
  const camel = raw.procurementRequestId;
  const pascal = raw.ProcurementRequestId;
  const s = (typeof camel === 'string' ? camel : typeof pascal === 'string' ? pascal : '').trim();
  return s;
}

export function mapApiRfqSummary(raw: Record<string, unknown>): RequestForQuoteSummary {
  const rfqId = String(raw.id ?? '').trim();
  let procurementRequestId = procurementRequestIdFromRfqApi(raw);
  if (procurementRequestId && rfqId && procurementRequestId === rfqId) {
    procurementRequestId = '';
  }
  return {
    id: String(raw.id ?? ''),
    rfqNumber: String(raw.rfqNumber ?? ''),
    procurementRequestId,
    requestNumber: String(raw.requestNumber ?? ''),
    region: String(raw.region ?? ''),
    status: normalizeRfqStatusFromApi(raw.status),
    createdAtUtc: String(raw.createdAtUtc ?? ''),
    updatedAtUtc: String(raw.updatedAtUtc ?? '')
  };
}

export interface RfqMaterialLineQuoteRow {
  itemId: string;
  unitPrice: number;
  mill: string;
  countryOfOrigin: string;
  requiredQtyFt: number;
  totalPipeCost: number;
  truckloadCount: number;
  costPerTruckload: number;
  truckingCost: number;
  totalLineCost: number;
}

/** RFQ workflow log entry (API camelCase). */
export interface RfqActivityEntryRow {
  atUtc: string;
  actorDisplayName: string;
  actorUsername?: string | null;
  message: string;
}

export interface RfqVendorInvitationRow {
  vendorId: string;
  vendorName: string;
  vendorEmail: string;
  responseStatus: string;
  invitationSentAtUtc?: string | null;
  quoteReceivedAtUtc?: string | null;
  quoteIncoterms?: string | null;
  freightAmount?: number | null;
  miscellaneousFeeAmount?: number | null;
  lineQuotes?: RfqMaterialLineQuoteRow[] | null;
  attachmentBlobUrl?: string | null;
  attachmentFileName?: string | null;
}

/** Vendors already emailed a quote link or who submitted a quote cannot be removed from the RFQ (server enforces). */
export function isRfqVendorRemovalLocked(inv: RfqVendorInvitationRow | undefined): boolean {
  if (!inv) {
    return false;
  }
  if (inv.invitationSentAtUtc?.trim()) {
    return true;
  }
  return inv.responseStatus.replace(/\s/g, '').toLowerCase() === 'quotereceived';
}

export interface RequestForQuoteDetail {
  id: string;
  rfqNumber: string;
  procurementRequestId: string;
  requestNumber: string;
  region: string;
  status: string;
  incotermsSnapshot: string;
  requirements: string;
  /** Deep copy of PR material lines at RFQ creation (same shape as procurement request materials). */
  materialsSnapshot: MaterialItem[];
  vendors: RfqVendorInvitationRow[];
  selectedVendorId?: string | null;
  submittedForApprovalAtUtc?: string | null;
  operationsDecisionComment?: string | null;
  operationsDecisionAtUtc?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  activityLog: RfqActivityEntryRow[];
}

/** Values for the PO create form, derived from the operations-approved vendor quote. */
export interface RfqPoQuotePrefill {
  incoterms: string;
  unitCost: string;
  freightAmount: string;
  miscellaneousFeeAmount: string;
  lineDescription: string;
}

function formatUsdInput(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) {
    return '';
  }
  return String(n);
}

/**
 * When an RFQ is approved, map the awarded vendor’s submitted quote onto PO fields
 * (incoterms prefer vendor quote; unit cost from the line quote matching the primary material item).
 */
export function poPrefillFromApprovedRfqQuote(
  rfq: RequestForQuoteDetail,
  selectedRequest: ProcurementRequest
): RfqPoQuotePrefill | null {
  if (normalizeRfqStatus(rfq.status) !== 'approved') {
    return null;
  }
  const vid = rfq.selectedVendorId?.trim() ?? '';
  if (!vid) {
    return null;
  }
  const inv = rfq.vendors.find((v) => v.vendorId === vid);
  if (!inv) {
    return null;
  }

  const inc = (inv.quoteIncoterms?.trim() || rfq.incotermsSnapshot?.trim() || '').trim();

  const primary = primaryMaterialLine(selectedRequest);
  const primaryItemId = primary?.itemId?.trim() ?? '';
  const lines = inv.lineQuotes ?? [];
  let unitPrice: number | undefined;
  if (lines.length > 0) {
    const match =
      primaryItemId ? lines.find((l) => (l.itemId ?? '').trim() === primaryItemId) : undefined;
    unitPrice = match?.unitPrice ?? lines[0]?.unitPrice;
  }
  const unitCost =
    unitPrice !== undefined && !Number.isNaN(unitPrice) && unitPrice >= 0 ? String(unitPrice) : '';

  return {
    incoterms: inc,
    unitCost,
    freightAmount: formatUsdInput(inv.freightAmount),
    miscellaneousFeeAmount: formatUsdInput(inv.miscellaneousFeeAmount),
    lineDescription: (primary?.description ?? '').trim()
  };
}

function mapVendorRow(raw: Record<string, unknown>): RfqVendorInvitationRow {
  return {
    vendorId: String(raw.vendorId ?? ''),
    vendorName: String(raw.vendorName ?? ''),
    vendorEmail: String(raw.vendorEmail ?? ''),
    responseStatus: String(raw.responseStatus ?? ''),
    invitationSentAtUtc: raw.invitationSentAtUtc ? String(raw.invitationSentAtUtc) : null,
    quoteReceivedAtUtc: raw.quoteReceivedAtUtc ? String(raw.quoteReceivedAtUtc) : null,
    quoteIncoterms: raw.quoteIncoterms != null ? String(raw.quoteIncoterms) : null,
    freightAmount:
      raw.freightAmount === undefined || raw.freightAmount === null ? null : Number(raw.freightAmount),
    miscellaneousFeeAmount:
      raw.miscellaneousFeeAmount === undefined || raw.miscellaneousFeeAmount === null
        ? null
        : Number(raw.miscellaneousFeeAmount),
    lineQuotes: Array.isArray(raw.lineQuotes)
      ? (raw.lineQuotes as Record<string, unknown>[]).map((l) => ({
          itemId: String(l.itemId ?? ''),
          unitPrice: Number(l.unitPrice ?? 0),
          mill: String(l.mill ?? ''),
          countryOfOrigin: String(l.countryOfOrigin ?? ''),
          requiredQtyFt: Number(l.requiredQtyFt ?? 0),
          totalPipeCost: Number(l.totalPipeCost ?? 0),
          truckloadCount: Number(l.truckloadCount ?? 0),
          costPerTruckload: Number(l.costPerTruckload ?? 0),
          truckingCost: Number(l.truckingCost ?? 0),
          totalLineCost: Number(l.totalLineCost ?? 0)
        }))
      : null,
    attachmentBlobUrl: raw.attachmentBlobUrl != null ? String(raw.attachmentBlobUrl) : null,
    attachmentFileName: raw.attachmentFileName != null ? String(raw.attachmentFileName) : null
  };
}

function mapRfqActivityEntry(raw: Record<string, unknown>): RfqActivityEntryRow {
  return {
    atUtc: String(raw.atUtc ?? ''),
    actorDisplayName: String(raw.actorDisplayName ?? ''),
    actorUsername: raw.actorUsername != null ? String(raw.actorUsername) : null,
    message: String(raw.message ?? '')
  };
}

function normalizeRfqActivityLog(raw: unknown): RfqActivityEntryRow[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return (raw as Record<string, unknown>[]).map(mapRfqActivityEntry);
}

export function mapApiRequestForQuote(raw: Record<string, unknown>): RequestForQuoteDetail {
  const vendorsRaw = raw.vendors;
  const vendors: RfqVendorInvitationRow[] = Array.isArray(vendorsRaw)
    ? (vendorsRaw as Record<string, unknown>[]).map(mapVendorRow)
    : [];

  const matsRaw = raw.materialsSnapshot;
  const materialsSnapshot: MaterialItem[] = Array.isArray(matsRaw)
    ? (matsRaw as Record<string, unknown>[]).map((x) => mapMaterialItemFromApi(x))
    : [];

  const rfqId = String(raw.id ?? '').trim();
  let procurementRequestId = procurementRequestIdFromRfqApi(raw);
  if (procurementRequestId && rfqId && procurementRequestId === rfqId) {
    procurementRequestId = '';
  }

  return {
    id: String(raw.id ?? ''),
    rfqNumber: String(raw.rfqNumber ?? ''),
    procurementRequestId,
    requestNumber: String(raw.requestNumber ?? ''),
    region: String(raw.region ?? ''),
    status: normalizeRfqStatusFromApi(raw.status),
    incotermsSnapshot: String(raw.incotermsSnapshot ?? ''),
    requirements: String(raw.requirements ?? ''),
    materialsSnapshot,
    vendors,
    selectedVendorId: raw.selectedVendorId != null ? String(raw.selectedVendorId) : null,
    submittedForApprovalAtUtc: raw.submittedForApprovalAtUtc
      ? String(raw.submittedForApprovalAtUtc)
      : null,
    operationsDecisionComment:
      raw.operationsDecisionComment != null ? String(raw.operationsDecisionComment) : null,
    operationsDecisionAtUtc: raw.operationsDecisionAtUtc ? String(raw.operationsDecisionAtUtc) : null,
    createdAtUtc: String(raw.createdAtUtc ?? ''),
    updatedAtUtc: String(raw.updatedAtUtc ?? ''),
    activityLog: normalizeRfqActivityLog(raw.activityLog)
  };
}
